/** Risk appetite (screens §P4.8): one meter per limit from the latest released KPIs. Breach = critical + word; inherited = warn + word. */
import { formatKpi } from "../format";
import { GxTerm } from "../theme/GxTerm";
import { GxPill, type PillTone } from "../theme/GxPill";
import { GxCard } from "../theme/primitives";
import type { LimitRow, LimitStatus } from "./derive";

const STATUS: Readonly<Record<LimitStatus, { readonly color: string; readonly word: string | null }>> = {
  ok: { color: "var(--navy)", word: null },
  watch: { color: "var(--warn)", word: "near" },
  breach: { color: "var(--crit)", word: "breach" },
  inherited: { color: "var(--warn)", word: "Inherited · plan needed" },
};

function summary(rows: readonly LimitRow[]): { readonly tone: PillTone; readonly text: string } {
  const breaches = rows.filter((r) => r.status === "breach").length;
  const inherited = rows.filter((r) => r.status === "inherited").length;
  const near = rows.filter((r) => r.status === "watch").length;
  if (breaches > 0) return { tone: "crit", text: `${breaches} breach${breaches === 1 ? "" : "es"}${near > 0 ? ` · ${near} near` : ""}` };
  if (inherited > 0) return { tone: "warn", text: `${inherited} inherited` };
  if (near > 0) return { tone: "warn", text: `${near} near a limit` };
  return { tone: "good", text: "Within your limits" };
}

export function RiskAppetite({ rows }: { readonly rows: readonly LimitRow[] }) {
  const head = summary(rows);
  // A team that saved no limits is still held to the defaults: they are shown and named, never "no limits" beside a breach.
  const defaults = rows.some((r) => r.isDefault === true);
  // No limits: a one-line row, not a card with a hole in it.
  if (rows.length === 0) return <p className="gx-small gx-muted gx-cockpit-nolimits" data-testid="cockpit.limits"><b>Risk appetite</b> · Your team set no limits in Take the Chair.</p>;
  return (
    <GxCard title={defaults ? "Risk appetite: the game's default limits" : "Risk appetite"} aside={rows.length === 0 ? null : <GxPill tone={head.tone}>{head.tone === "crit" ? "!" : "●"} {head.text}</GxPill>} testId="cockpit.limits">
      <div className="gx-card-b gx-cockpit-watch gx-num">
        {defaults ? <p className="gx-small gx-muted" data-testid="cockpit.limits.default">Your team saved no limits of its own in Take the Chair, so the game&apos;s default limits apply.</p> : null}
        {rows.map((r) => {
          const s = STATUS[r.status];
          return (
            <div key={r.kpi} className="gx-cockpit-watch-row" data-testid={`cockpit.limit.${r.kpi}`} data-status={r.status}>
              <span className="gx-cockpit-watch-m"><GxTerm term={r.kpi}>{r.label}</GxTerm>{s.word === null ? null : <GxPill tone={r.status === "breach" ? "crit" : "warn"}>{r.status === "breach" ? "!" : "●"} {s.word}</GxPill>}</span>
              <b style={{ color: r.status === "ok" ? undefined : s.color }}>{formatKpi(r.kpi, r.value)}</b>
              <span className="gx-cockpit-watch-lim">{r.limitText}</span>
              <div className="gx-cockpit-meter"><i style={{ width: `${r.fillPct}%`, background: s.color }} /><b style={{ left: `${r.tickPct}%` }} /></div>
            </div>
          );
        })}
      </div>
    </GxCard>
  );
}
